import type { FormEvent, ReactNode } from 'react'
import type { BookingVehicle, VehicleSelection } from '@/types'
import type { BookingErrors, BookingField, BookingInput } from '@/lib/booking'

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import {
    Car,
    ChevronDown,
    CircleAlert,
    CircleCheck,
    LoaderCircle,
    MapPin,
    Minus,
    Navigation,
    Plus,
    Send
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClass } from '@/lib/ui'
import { formatLongDate, pluralize } from '@/lib/format'
import { getVehicleName } from '@/lib/vehicle-helpers'
import {
    BOOKING_FIELD_ORDER,
    BOOKING_LIMITS,
    createEmptyBooking,
    normalizeBooking,
    toDateKey,
    validateBooking
} from '@/lib/booking'
import { SERVICE_TYPES } from '@/constants/site'

interface BookingFormProps {
    vehicles: Array<BookingVehicle>
    selection?: VehicleSelection | null
    lockedVehicle?: BookingVehicle | null
}

interface FieldProps {
    id: string
    label: string
    error?: string
    hint?: string
    optional?: boolean
    className?: string
    children: ReactNode
}

interface BookingResponse {
    ok?: boolean
    message?: string
    errors?: BookingErrors
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

const controlClass = (hasError: boolean, className?: string): string => cn(
    'h-12 w-full rounded-xl border bg-background px-4 text-sm text-foreground transition-[border-color,box-shadow] placeholder:text-muted-foreground/70 focus:outline-none focus-visible:ring-2',
    hasError ? 'border-destructive/70 focus-visible:ring-destructive/25' : 'border-input focus-visible:border-gold/60 focus-visible:ring-ring/30',
    className
)

const describedBy = (id: string, error?: string, hint?: string): string | undefined => {
    if (error) return `${id}-error`
    if (hint) return `${id}-hint`

    return undefined
}

const Field = ({ id, label, error, hint, optional = false, className, children }: FieldProps) => (
    <div className={cn('flex flex-col gap-1.5', className)}>
        <label htmlFor={id} className='flex items-baseline justify-between gap-2 text-sm font-medium text-foreground'>
            {label}
            {optional && <span className='text-xs font-normal text-muted-foreground'>Opcional</span>}
        </label>

        {children}

        {error && (
            <p id={`${id}-error`} className='flex items-start gap-1.5 text-xs text-destructive'>
                <CircleAlert className='mt-px size-3.5 shrink-0' aria-hidden='true' />
                {error}
            </p>
        )}

        {!error && hint && (
            <p id={`${id}-hint`} className='text-xs text-muted-foreground'>
                {hint}
            </p>
        )}
    </div>
)

export default function BookingForm({ vehicles, selection = null, lockedVehicle = null }: BookingFormProps) {
    const formId = useId()
    const headingRef = useRef<HTMLHeadingElement>(null)
    const [values, setValues] = useState<BookingInput>(() => createEmptyBooking(lockedVehicle?.id ?? selection?.vehicleId ?? ''))
    const [errors, setErrors] = useState<BookingErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [submitted, setSubmitted] = useState<BookingInput | null>(null)
    const [today, setToday] = useState('')

    // The visitor's own date, read after mount so server and browser render the same markup.
    useEffect(() => {
        setToday(toDateKey(new Date()))
    }, [])

    // A "Reservar" click on a card preselects that vehicle and moves focus here.
    useEffect(() => {
        if (!selection) return

        setValues(current => ({ ...current, vehiculo_id: selection.vehicleId }))
        setErrors(current => ({ ...current, vehiculo_id: undefined, pasajeros: undefined }))
        setStatus(current => current === 'success' ? 'idle' : current)
        headingRef.current?.focus({ preventScroll: true })
    }, [selection])

    useEffect(() => {
        if (status === 'success') headingRef.current?.focus({ preventScroll: true })
    }, [status])

    const vehicleId = lockedVehicle?.id ?? values.vehiculo_id
    const selectedVehicle = useMemo(() => lockedVehicle ?? vehicles.find(vehicle => vehicle.id === vehicleId) ?? null, [lockedVehicle, vehicles, vehicleId])
    const maxPassengers = Math.min(selectedVehicle?.pasajeros ?? BOOKING_LIMITS.pasajeros, BOOKING_LIMITS.pasajeros)

    const fieldId = (field: BookingField): string => `${formId}-${field}`

    const setField = <K extends keyof BookingInput>(field: K, value: BookingInput[K]) => {
        setValues(current => ({ ...current, [field]: value }))

        if (field in errors) {
            setErrors(current => {
                const next = { ...current }

                delete next[field as BookingField]

                return next
            })
        }
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const payload = normalizeBooking({ ...values, vehiculo_id: vehicleId })
        const nextErrors = validateBooking(payload, today || toDateKey(new Date()), selectedVehicle)
        const firstInvalid = BOOKING_FIELD_ORDER.find(field => nextErrors[field])

        setErrors(nextErrors)

        if (firstInvalid) {
            document.getElementById(fieldId(firstInvalid))?.focus()

            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/reservas', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })

            const data = await response.json().catch(() => null) as BookingResponse | null

            if (!response.ok || !data?.ok) {
                if (data?.errors) setErrors(data.errors)

                setServerMessage(data?.message ?? 'No hemos podido enviar tu solicitud. Inténtalo de nuevo en unos minutos.')
                setStatus('error')

                return
            }

            setSubmitted(payload)
            setStatus('success')
        }
        catch {
            setServerMessage('No hemos podido conectar. Revisa tu conexión a internet e inténtalo de nuevo.')
            setStatus('error')
        }
    }

    const startOver = () => {
        setValues(createEmptyBooking(lockedVehicle?.id ?? submitted?.vehiculo_id ?? ''))
        setErrors({})
        setSubmitted(null)
        setServerMessage('')
        setStatus('idle')
    }

    const cardClass = 'rounded-3xl border border-border/70 bg-card p-6 text-foreground shadow-[0_40px_80px_-40px_rgb(0_0_0/0.6)] sm:p-8'

    if (status === 'success' && submitted) {
        const firstName = submitted.nombre.split(/\s+/)[0]

        return (
            <div className={cardClass}>
                <div role='status' className='flex flex-col items-center py-10 text-center sm:py-14'>
                    <span className='flex size-16 items-center justify-center rounded-full bg-secondary text-gold'>
                        <CircleCheck className='size-7' aria-hidden='true' />
                    </span>

                    <h3 ref={headingRef} tabIndex={-1} className='mt-6 font-heading text-3xl focus:outline-none'>
                        Solicitud enviada
                    </h3>

                    <p className='mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground'>
                        Gracias, {firstName}. Hemos recibido tu solicitud para el {formatLongDate(submitted.fecha)} a las {submitted.hora}. Revisaremos la disponibilidad y te responderemos por email o por teléfono.
                    </p>

                    <button type='button' onClick={startOver} className={buttonClass('outline', 'md', 'mt-8')}>
                        Hacer otra solicitud
                    </button>
                </div>
            </div>
        )
    }

    const submitting = status === 'submitting'

    return (
        <form noValidate onSubmit={handleSubmit} className={cardClass} aria-busy={submitting}>
            <h3 ref={headingRef} tabIndex={-1} className='font-heading text-2xl focus:outline-none sm:text-3xl'>
                Datos de la reserva
            </h3>

            <p className='mt-1.5 text-sm text-muted-foreground'>
                Te responderemos con la disponibilidad y el precio final.
            </p>

            {lockedVehicle && (
                <div className='mt-6 flex items-center gap-3 rounded-2xl bg-secondary/70 px-4 py-3 text-sm'>
                    <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-card text-gold'>
                        <Car className='size-4' aria-hidden='true' />
                    </span>

                    <p className='leading-snug'>
                        <span className='block text-xs text-muted-foreground'>Vehículo seleccionado</span>
                        <span className='font-semibold'>
                            <span data-mdlfy-cms={`vehiculos:${lockedVehicle.id}:marca`}>{lockedVehicle.marca}</span>
                            {' '}
                            <span data-mdlfy-cms={`vehiculos:${lockedVehicle.id}:modelo`}>{lockedVehicle.modelo}</span>
                        </span>
                        <span className='text-muted-foreground'>
                            {' '}· hasta <span>{lockedVehicle.pasajeros}</span> {pluralize(lockedVehicle.pasajeros, 'pasajero', 'pasajeros')}
                        </span>
                    </p>
                </div>
            )}

            <div className='mt-6 grid gap-5 sm:grid-cols-2'>
                <Field id={fieldId('servicio')} label='Tipo de servicio' error={errors.servicio} className={cn(lockedVehicle && 'sm:col-span-2')}>
                    <div className='relative'>
                        <select
                            id={fieldId('servicio')}
                            value={values.servicio}
                            onChange={event => setField('servicio', event.target.value)}
                            aria-invalid={Boolean(errors.servicio)}
                            aria-describedby={describedBy(fieldId('servicio'), errors.servicio)}
                            className={controlClass(Boolean(errors.servicio), cn('cursor-pointer appearance-none pr-10', !values.servicio && 'text-muted-foreground'))}
                        >
                            <option value='' disabled>
                                Elige una opción
                            </option>

                            {SERVICE_TYPES.map(service => (
                                <option key={service} value={service}>
                                    {service}
                                </option>
                            ))}
                        </select>

                        <ChevronDown className='pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                    </div>
                </Field>

                {!lockedVehicle && (
                    <Field id={fieldId('vehiculo_id')} label='Vehículo' optional error={errors.vehiculo_id}>
                        <div className='relative'>
                            <select
                                id={fieldId('vehiculo_id')}
                                value={values.vehiculo_id}
                                onChange={event => {
                                    setField('vehiculo_id', event.target.value)
                                    setErrors(current => ({ ...current, pasajeros: undefined }))
                                }}
                                aria-invalid={Boolean(errors.vehiculo_id)}
                                aria-describedby={describedBy(fieldId('vehiculo_id'), errors.vehiculo_id)}
                                className={controlClass(Boolean(errors.vehiculo_id), 'cursor-pointer appearance-none pr-10')}
                            >
                                <option value=''>
                                    Sin preferencia, aconsejadme
                                </option>

                                {vehicles.map(vehicle => (
                                    <option key={vehicle.id} value={vehicle.id}>
                                        {`${getVehicleName(vehicle)} · hasta ${vehicle.pasajeros} ${pluralize(vehicle.pasajeros, 'pasajero', 'pasajeros')}`}
                                    </option>
                                ))}
                            </select>

                            <ChevronDown className='pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                        </div>
                    </Field>
                )}

                <Field id={fieldId('fecha')} label='Fecha' error={errors.fecha}>
                    <input
                        id={fieldId('fecha')}
                        type='date'
                        value={values.fecha}
                        min={today || undefined}
                        onChange={event => setField('fecha', event.target.value)}
                        aria-invalid={Boolean(errors.fecha)}
                        aria-describedby={describedBy(fieldId('fecha'), errors.fecha)}
                        className={controlClass(Boolean(errors.fecha))}
                    />
                </Field>

                <Field id={fieldId('hora')} label='Hora de recogida' error={errors.hora}>
                    <input
                        id={fieldId('hora')}
                        type='time'
                        step={300}
                        value={values.hora}
                        onChange={event => setField('hora', event.target.value)}
                        aria-invalid={Boolean(errors.hora)}
                        aria-describedby={describedBy(fieldId('hora'), errors.hora)}
                        className={controlClass(Boolean(errors.hora))}
                    />
                </Field>

                <Field id={fieldId('recogida')} label='Lugar de recogida' error={errors.recogida} className='sm:col-span-2'>
                    <div className='relative'>
                        <MapPin className='pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-gold' aria-hidden='true' />

                        <input
                            id={fieldId('recogida')}
                            type='text'
                            value={values.recogida}
                            maxLength={BOOKING_LIMITS.recogida}
                            onChange={event => setField('recogida', event.target.value)}
                            placeholder='Dirección, hotel, estación o aeropuerto'
                            autoComplete='street-address'
                            aria-invalid={Boolean(errors.recogida)}
                            aria-describedby={describedBy(fieldId('recogida'), errors.recogida)}
                            className={controlClass(Boolean(errors.recogida), 'pl-11')}
                        />
                    </div>
                </Field>

                <Field id={fieldId('destino')} label='Destino' optional error={errors.destino} hint='Déjalo vacío si es un servicio por horas.'>
                    <div className='relative'>
                        <Navigation className='pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-gold' aria-hidden='true' />

                        <input
                            id={fieldId('destino')}
                            type='text'
                            value={values.destino}
                            maxLength={BOOKING_LIMITS.destino}
                            onChange={event => setField('destino', event.target.value)}
                            placeholder='¿A dónde vamos?'
                            autoComplete='off'
                            aria-invalid={Boolean(errors.destino)}
                            aria-describedby={describedBy(fieldId('destino'), errors.destino, 'hint')}
                            className={controlClass(Boolean(errors.destino), 'pl-11')}
                        />
                    </div>
                </Field>

                <Field
                    id={fieldId('pasajeros')}
                    label='Pasajeros'
                    error={errors.pasajeros}
                    hint={selectedVehicle ? `Este vehículo admite hasta ${selectedVehicle.pasajeros}.` : 'Si sois un grupo grande, indícalo en los comentarios.'}
                >
                    <div className={cn('flex h-12 items-center justify-between rounded-xl border bg-background px-2', errors.pasajeros ? 'border-destructive/70' : 'border-input')}>
                        <button
                            type='button'
                            onClick={() => setField('pasajeros', Math.max(1, values.pasajeros - 1))}
                            disabled={values.pasajeros <= 1}
                            aria-label='Quitar un pasajero'
                            className='flex size-9 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-35'
                        >
                            <Minus className='size-4' aria-hidden='true' />
                        </button>

                        <input
                            id={fieldId('pasajeros')}
                            type='number'
                            inputMode='numeric'
                            min={1}
                            max={maxPassengers}
                            value={values.pasajeros}
                            onChange={event => setField('pasajeros', Number.parseInt(event.target.value, 10) || 1)}
                            aria-invalid={Boolean(errors.pasajeros)}
                            aria-describedby={describedBy(fieldId('pasajeros'), errors.pasajeros, 'hint')}
                            className='h-full w-16 bg-transparent text-center text-sm font-semibold tabular-nums focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
                        />

                        <button
                            type='button'
                            onClick={() => setField('pasajeros', Math.min(maxPassengers, values.pasajeros + 1))}
                            disabled={values.pasajeros >= maxPassengers}
                            aria-label='Añadir un pasajero'
                            className='flex size-9 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-35'
                        >
                            <Plus className='size-4' aria-hidden='true' />
                        </button>
                    </div>
                </Field>
            </div>

            <div className='mt-8 flex items-center gap-4'>
                <p className='text-xs font-semibold uppercase tracking-[0.22em] text-gold'>
                    Tus datos
                </p>

                <span aria-hidden='true' className='h-px flex-1 bg-border' />
            </div>

            <div className='mt-5 grid gap-5 sm:grid-cols-2'>
                <Field id={fieldId('nombre')} label='Nombre y apellidos' error={errors.nombre}>
                    <input
                        id={fieldId('nombre')}
                        type='text'
                        value={values.nombre}
                        maxLength={BOOKING_LIMITS.nombre}
                        onChange={event => setField('nombre', event.target.value)}
                        autoComplete='name'
                        aria-invalid={Boolean(errors.nombre)}
                        aria-describedby={describedBy(fieldId('nombre'), errors.nombre)}
                        className={controlClass(Boolean(errors.nombre))}
                    />
                </Field>

                <Field id={fieldId('telefono')} label='Teléfono' error={errors.telefono}>
                    <input
                        id={fieldId('telefono')}
                        type='tel'
                        value={values.telefono}
                        maxLength={BOOKING_LIMITS.telefono}
                        onChange={event => setField('telefono', event.target.value)}
                        autoComplete='tel'
                        placeholder='+34 600 000 000'
                        aria-invalid={Boolean(errors.telefono)}
                        aria-describedby={describedBy(fieldId('telefono'), errors.telefono)}
                        className={controlClass(Boolean(errors.telefono))}
                    />
                </Field>

                <Field id={fieldId('email')} label='Email' error={errors.email} className='sm:col-span-2'>
                    <input
                        id={fieldId('email')}
                        type='email'
                        value={values.email}
                        maxLength={BOOKING_LIMITS.email}
                        onChange={event => setField('email', event.target.value)}
                        autoComplete='email'
                        placeholder='tu@email.com'
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy(fieldId('email'), errors.email)}
                        className={controlClass(Boolean(errors.email))}
                    />
                </Field>

                <Field id={fieldId('comentarios')} label='Comentarios' optional error={errors.comentarios} className='sm:col-span-2'>
                    <textarea
                        id={fieldId('comentarios')}
                        rows={4}
                        value={values.comentarios}
                        maxLength={BOOKING_LIMITS.comentarios}
                        onChange={event => setField('comentarios', event.target.value)}
                        placeholder='Número de vuelo, paradas intermedias, sillas infantiles, decoración para la boda…'
                        aria-invalid={Boolean(errors.comentarios)}
                        aria-describedby={describedBy(fieldId('comentarios'), errors.comentarios)}
                        className={controlClass(Boolean(errors.comentarios), 'h-auto resize-y py-3 leading-relaxed')}
                    />
                </Field>
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor={`${formId}-empresa_web`}>
                    Deja este campo vacío
                </label>

                <input
                    id={`${formId}-empresa_web`}
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.empresa_web}
                    onChange={event => setField('empresa_web', event.target.value)}
                />
            </div>

            {status === 'error' && serverMessage && (
                <div role='alert' className='mt-6 flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    {serverMessage}
                </div>
            )}

            <button type='submit' disabled={submitting} className={buttonClass('primary', 'lg', 'mt-8 w-full')}>
                {submitting
                    ? <LoaderCircle className='size-5 animate-spin' aria-hidden='true' />
                    : <Send className='size-4' aria-hidden='true' />}
                {submitting ? 'Enviando solicitud…' : 'Solicitar reserva'}
            </button>

            <p className='mt-4 text-center text-xs leading-relaxed text-muted-foreground'>
                Sin compromiso. Solo usaremos tus datos para gestionar esta solicitud.
            </p>
        </form>
    )
}
